// Partneraftaler: firmaer og foreninger (fx FDM, Ældre Sagen, Red Bull) med deres egne pakker og priser. Partnerens kunder
// booker på partnerens egen side (#/p/<slug>, som fx steamgreen.dk/fdm peger på) – den almindelige booking viser aldrig
// partnerpakker eller hvilke partnere der findes. En pakke bygger på en almindelig behandling (basis-ydelsen): steameren
// laver basis-ydelsen, tilvalgene er de samme som basis-ydelsens, men pris og varighed pr. biltype er partnerens egne.
// Bookingmotoren (motor.ts) regner på pakkerne som "virtuelle ydelser" med id'et 'pp:<pakke-id>'; ved bestillingen gemmes
// basis-ydelsen på bookingen sammen med partneren og pakken. Samme regler i testversionen og på serveren.
import { fejl } from './fejl.ts';
import { rens } from './validering.ts';
import type { MotorData } from './motor.ts';
import type { Biltype, Tilvalg, Ydelse, YdelsePris } from './typer.ts';

export type PartnerBetaling = 'kunde' | 'faktura';
/** Hvad partnerens side kræver, før kunden kan booke: intet, en fælles adgangskode eller kundens medarbejder-/medlemsnummer. */
export type PartnerKrav = 'ingen' | 'kode' | 'nummer';
export type PakkePris = { biltype_id: string; pris: number; varighed: number };   // øre ekskl. moms, minutter
export type PartnerPakke = {
  id: string; partner_id: string; navn: string; beskrivelse: string; ydelse_id: string;   // ydelse_id = basis-ydelsen
  priser: PakkePris[]; aktiv: boolean; sortering: number;
};
export type Partner = {
  id: string; slug: string; navn: string; logo: string | null; aktiv: boolean;
  brand_id: string | null;      // bookinger mærkes med brandet (fx SteamFox) – tom = virksomhedens eget
  betaling: PartnerBetaling;    // kunde = kunden betaler som normalt; faktura = partneren faktureres samlet
  krav: PartnerKrav; adgangskode: string | null; nummer_navn: string;   // fx "Medlemsnummer" eller "Medarbejdernummer"
  vis_standard: boolean;        // vis også de almindelige behandlinger på partnerens side (til normal pris)
  note: string;                 // intern note til kontoret
};
/** Det, kunden sender med fra partnerens side. */
export type PartnerAdgang = { slug: string; kode?: string; nr?: string };
/** Det, alle må se om partneren (aldrig adgangskode, note eller pakkernes interne data). */
export type PartnerOffentlig = Pick<Partner, 'slug' | 'navn' | 'logo' | 'krav' | 'nummer_navn' | 'betaling'>;

export const PAKKE = 'pp:';
export const pakkeYdelse = (pakke_id: string) => PAKKE + pakke_id;
export const erPakke = (ydelse_id: string) => ydelse_id.startsWith(PAKKE);
export const offentlig = (p: Partner): PartnerOffentlig => ({ slug: p.slug, navn: p.navn, logo: p.logo, krav: p.krav, nummer_navn: p.nummer_navn, betaling: p.betaling });

/** Navn → adresse ("Ældre Sagen" → "aeldre-sagen"). Samme regel som pixie_slug i databasen. */
export function slugFra(navn: string): string {
  return navn.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}
/** "Medlemsnummer" → "medlemsnummer" midt i en sætning – men "FDM-medlemsnummer" bevarer forkortelsen. */
export const iSaetning = (s: string) => (/^[A-ZÆØÅ][a-zæøå]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);
/** Partnerens bookinglink på virksomhedens domæne. */
export const partnerLink = (domaene: string, slug: string) => `https://${domaene.replace(/^https?:\/\//, '').replace(/\/+$/, '')}/#/p/${slug}`;

/** Find en aktiv partner ud fra adressen – ellers en fejl, der ikke afslører, om partneren findes. */
export function findPartner(partnere: Partner[], slug: string): Partner {
  const p = partnere.find((x) => x.slug === rens(slug, 40).toLowerCase() && x.aktiv);
  if (!p) throw fejl('Siden findes ikke. Tjek linket, eller book på forsiden.');
  return p;
}

/** Tjek kode eller nummer. Returnerer kundens nummer (til bookingen), når partneren kræver det. */
export function tjekAdgang(p: Partner, a: Pick<PartnerAdgang, 'kode' | 'nr'>): string | null {
  if (p.krav === 'kode') {
    const k = rens(a.kode ?? '', 60);
    if (!k) throw fejl('Skriv adgangskoden.', { kode: 'Skriv adgangskoden.' });
    if (k.toLowerCase() !== (p.adgangskode ?? '').trim().toLowerCase()) throw fejl('Forkert adgangskode.', { kode: 'Forkert adgangskode.' });
  }
  if (p.krav === 'nummer') {
    const nr = rens(a.nr ?? '', 40);
    if (nr.length < 2) throw fejl(`Skriv dit ${iSaetning(p.nummer_navn || 'nummer')}.`, { nr: 'Skal udfyldes.' });
    return nr;
  }
  return null;
}

/** Pakkens "virtuelle ydelse": basis-ydelsens felter (tjekliste, farve, kategori …) med pakkens navn og beskrivelse. */
function virtuel(p: PartnerPakke, basis: Ydelse): Ydelse {
  return { ...basis, id: pakkeYdelse(p.id), navn: p.navn, beskrivelse: p.beskrivelse || basis.beskrivelse, aktiv: p.aktiv && basis.aktiv, online: true, sortering: p.sortering };
}
function virtuelle(ydelser: Ydelse[], biltyper: Pick<Biltype, 'id'>[], pakker: PartnerPakke[]) {
  const y: Ydelse[] = []; const priser: YdelsePris[] = [];
  for (const p of [...pakker].sort((a, b) => a.sortering - b.sortering || a.navn.localeCompare(b.navn, 'da'))) {
    const basis = ydelser.find((x) => x.id === p.ydelse_id);
    if (!basis || !p.aktiv) continue;
    y.push(virtuel(p, basis));
    for (const x of p.priser) if (biltyper.some((b) => b.id === x.biltype_id)) priser.push({ ydelse_id: pakkeYdelse(p.id), biltype_id: x.biltype_id, pris: x.pris, varighed: x.varighed });
  }
  return { ydelser: y, priser };
}

/** En pakke arver basis-ydelsens tilvalgsregler: tilvalg, der må vælges til basis-ydelsen, må også vælges til pakken. */
function arvTilvalg<T extends { ydelser?: string[] | null }>(tilvalg: T[], pakker: PartnerPakke[]): T[] {
  return tilvalg.map((t) => {
    if (!t.ydelser?.length) return t;
    const ekstra = pakker.filter((p) => t.ydelser!.includes(p.ydelse_id)).map((p) => pakkeYdelse(p.id));
    return ekstra.length ? { ...t, ydelser: [...t.ydelser, ...ekstra] } : t;
  });
}

/**
 * Motorens data på partnerens side: kun partnerens pakker (plus de almindelige behandlinger, hvis partneren viser dem),
 * og steamere, der laver basis-ydelsen, kan også tage pakken.
 */
export function partnerMotor<D extends MotorData>(d: D, p: Partner, pakker: PartnerPakke[]): D {
  const v = virtuelle(d.ydelser, d.biltyper, pakker.filter((x) => x.partner_id === p.id));
  const basis = (id: string) => pakker.find((x) => pakkeYdelse(x.id) === id)?.ydelse_id;
  return {
    ...d,
    ydelser: [...(p.vis_standard ? d.ydelser : []), ...v.ydelser],
    priser: [...(p.vis_standard ? d.priser : []), ...v.priser],
    tilvalg: arvTilvalg(d.tilvalg, pakker.filter((x) => x.partner_id === p.id)),
    steamere: d.steamere.map((s) => (s.ydelser?.length ? { ...s, ydelser: [...s.ydelser, ...v.ydelser.filter((y) => s.ydelser!.includes(basis(y.id)!)).map((y) => y.id)] } : s)),
  };
}

export type PartnerData = { partnere?: Partner[]; partnerpakker?: PartnerPakke[] };
/** Partnerens side: find partneren, tjek adgangen og giv motorens data for siden. */
export function partnerKontekst<D extends MotorData & PartnerData>(d: D, a: PartnerAdgang): { partner: Partner; nr: string | null; data: D } {
  const partner = findPartner(d.partnere ?? [], a.slug);
  const nr = tjekAdgang(partner, a);
  return { partner, nr, data: partnerMotor(d, partner, d.partnerpakker ?? []) };
}

/**
 * Bookingsidens katalog på partnerens side. k er det almindelige katalog (evt. under partnerens brand), alleYdelser
 * alle virksomhedens ydelser (basis-ydelsen behøver ikke at kunne bookes online). Medlemskab og partnerlisten vises ikke.
 */
export function katalogForPartner<K extends { ydelser: Ydelse[]; priser: YdelsePris[]; biltyper: Pick<Biltype, 'id'>[]; medlemskab: { aktiv: boolean }; partnere: string[] }>(
  k: K, alleYdelser: Ydelse[], p: Partner, pakker: PartnerPakke[],
): K & { partner: PartnerOffentlig } {
  const v = virtuelle(alleYdelser, k.biltyper, pakker.filter((x) => x.partner_id === p.id));
  return {
    ...k,
    ydelser: [...(p.vis_standard ? k.ydelser : []), ...v.ydelser],
    priser: [...(p.vis_standard ? k.priser : []), ...v.priser],
    ...('tilvalg' in k && Array.isArray((k as { tilvalg?: unknown }).tilvalg) ? { tilvalg: arvTilvalg((k as unknown as { tilvalg: Tilvalg[] }).tilvalg, pakker.filter((x) => x.partner_id === p.id)) } : {}),
    medlemskab: { ...k.medlemskab, aktiv: false }, partnere: [], partner: offentlig(p),
  };
}

/** Pakkens id → basis-ydelsen og pakken (til bookingen). Almindelige ydelser er deres egen basis. */
export function basisFor(ydelse_id: string, pakker: PartnerPakke[]): { ydelse_id: string; pakke: PartnerPakke | null } {
  if (!erPakke(ydelse_id)) return { ydelse_id, pakke: null };
  const pakke = pakker.find((x) => pakkeYdelse(x.id) === ydelse_id);
  if (!pakke) throw fejl('Pakken findes ikke længere. Vælg en anden.');
  return { ydelse_id: pakke.ydelse_id, pakke };
}

const LOGO = /^(data:image\/(png|jpeg|webp|svg\+xml)[;,]|https:\/\/)/;
/** Tjek og ryd en partner og dens pakker op, før de gemmes (kontoret). Kaster en fejl med de felter, der er galt. */
export function normPartner(p: Partner, pakker: PartnerPakke[], katalog: { ydelser: Pick<Ydelse, 'id'>[]; biltyper: Pick<Biltype, 'id'>[]; brands: string[] }): { partner: Partner; pakker: PartnerPakke[] } {
  const f: Record<string, string> = {};
  const navn = rens(p.navn, 80); if (navn.length < 2) f.navn = 'Skriv partnerens navn.';
  const slug = rens(p.slug, 40).toLowerCase() || slugFra(navn);
  if (!/^[a-z0-9][a-z0-9-]{1,39}$/.test(slug)) f.slug = 'Kun små bogstaver, tal og bindestreg (2–40 tegn).';
  if (p.logo && !LOGO.test(p.logo)) f.logo = 'Logoet skal være et billede.';
  if (p.logo && p.logo.length > 400_000) f.logo = 'Logoet er for stort (højst ca. 300 KB).';
  const brand_id = p.brand_id || null;
  if (brand_id && !katalog.brands.includes(brand_id)) f.brand_id = 'Vælg et af jeres subdomæner.';
  const krav: PartnerKrav = (['ingen', 'kode', 'nummer'] as const).includes(p.krav) ? p.krav : 'ingen';
  const adgangskode = krav === 'kode' ? rens(p.adgangskode ?? '', 60) : null;
  if (krav === 'kode' && (adgangskode ?? '').length < 4) f.adgangskode = 'Mindst 4 tegn.';
  const nummer_navn = krav === 'nummer' ? rens(p.nummer_navn, 40) || 'Medlemsnummer' : '';
  const ud: PartnerPakke[] = pakker.map((x, i) => {
    const n = rens(x.navn, 80);
    if (n.length < 2) f[`pakke${i}_navn`] = 'Skriv pakkens navn.';
    if (!katalog.ydelser.some((y) => y.id === x.ydelse_id)) f[`pakke${i}_ydelse`] = 'Vælg den behandling, pakken bygger på.';
    const priser = x.priser.filter((q) => katalog.biltyper.some((b) => b.id === q.biltype_id));
    if (!priser.length) f[`pakke${i}_priser`] = 'Sæt pris for mindst én biltype.';
    for (const q of priser) {
      if (!Number.isInteger(q.pris) || q.pris < 0 || q.pris > 10_000_000) f[`pakke${i}_priser`] = 'Priserne skal være beløb i kroner.';
      if (!Number.isInteger(q.varighed) || q.varighed < 15 || q.varighed > 720) f[`pakke${i}_priser`] = 'Varigheden skal være 15–720 minutter.';
    }
    return { id: x.id, partner_id: p.id, navn: n, beskrivelse: rens(x.beskrivelse, 500), ydelse_id: x.ydelse_id, priser: [...new Map(priser.map((q) => [q.biltype_id, q])).values()],
      aktiv: x.aktiv !== false, sortering: i };
  });
  if (Object.keys(f).length) throw fejl('Tjek de markerede felter.', f);
  return {
    partner: { id: p.id, slug, navn, logo: p.logo || null, aktiv: p.aktiv !== false, brand_id, betaling: p.betaling === 'faktura' ? 'faktura' : 'kunde', krav, adgangskode, nummer_navn,
      vis_standard: !!p.vis_standard, note: String(p.note ?? '').trim().slice(0, 2000) },
    pakker: ud,
  };
}

export const TOM_PARTNER = (): Partner => ({ id: '', slug: '', navn: '', logo: null, aktiv: true, brand_id: null, betaling: 'kunde', krav: 'ingen', adgangskode: null, nummer_navn: '', vis_standard: false, note: '' });
